<script setup>
import { ref, computed } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiSegmented from '@/Components/UI/UiSegmented.vue'
import UiTable from '@/Components/UI/UiTable.vue'
import RentalCard from '@/Components/Cards/RentalCard.vue'
import UiStatusPill from '@/Components/UI/UiStatusPill.vue'
import RentalStatusPill from '@/Components/UI/RentalStatusPill.vue'
import EmptyState from '@/Components/UI/EmptyState.vue'
import LayoutToggle from '@/Components/UI/LayoutToggle.vue'
import PeriodSelector from '@/Components/UI/PeriodSelector.vue'
import Icon from '@/Components/UI/Icon.vue'
import { useCurrency } from '@/composables/useCurrency.js'
import { useLayoutMode } from '@/composables/useLayoutMode.js'
import { setPageHeaderCount } from '@/composables/usePageHeader.js'

const LAYOUT_MODES = [
  { v: 'table',   label: 'Table',   icon: 'layout-table' },
  { v: 'cards',   label: 'Cards',   icon: 'layout-cards' },
  { v: 'compact', label: 'Compact', icon: 'layout-list'  },
]

defineOptions({ layout: AppLayout })
const { t } = useI18n()
const { formatMoney, formatDateTime } = useCurrency()

const props = defineProps({
  rentals: { type: Array, default: () => [] },
  meta:    { type: Object, default: () => ({}) },
  filters: { type: Object, default: () => ({}) },
  period:        { type: String, default: 'this_month' },
  period_label:  { type: String, default: '' },
  period_from:   { type: String, default: '' },
  period_to:     { type: String, default: '' },
})

const search = ref(props.filters?.q || '')
const activeStatus = ref(props.filters?.status || 'all')
const layoutMode = useLayoutMode('rentals', LAYOUT_MODES, 'table')
let searchTimer = null

const statusTone = (s) => ({
  active: 'ok', pending: 'warn', returned: 'info', cancelled: 'muted', overdue: 'danger',
}[s] || 'muted')

setPageHeaderCount(computed(() => props.meta?.total ?? 0), (n) => t('rentals.count', { n }).replace(/^\d+\s*/, ''))

const periodParams = computed(() => {
  const p = { period: props.period }
  if (props.period === 'custom') {
    if (props.period_from) p.date_from = props.period_from
    if (props.period_to)   p.date_to   = props.period_to
  }
  return p
})

const filter = (status) => {
  const next = status === activeStatus.value ? 'all' : status
  activeStatus.value = next
  router.get('/rentals', { ...periodParams.value, status: next === 'all' ? '' : next, q: search.value }, { preserveState: true })
}

const onSearch = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    router.get('/rentals', { ...periodParams.value, status: activeStatus.value === 'all' ? '' : activeStatus.value, q: search.value }, { preserveState: true })
  }, 200)
}

const mainStatuses     = ['all', 'returned', 'cancelled']
const allTimeStatuses  = ['active', 'pending', 'overdue', 'outstanding', 'deposits']
const statusOptions = computed(() => mainStatuses.map((s) => ({
  value: s,
  label: t(`rentals.status.${s}`),
})))
const allTimeOptions = computed(() => allTimeStatuses.map((s) => ({
  value: s,
  label: t(`rentals.status.${s}`),
})))
const mainModel     = computed(() => mainStatuses.includes(activeStatus.value) ? activeStatus.value : '')
const allTimeModel  = computed(() => allTimeStatuses.includes(activeStatus.value) ? activeStatus.value : '')
const isAllTime     = computed(() => allTimeStatuses.includes(activeStatus.value))

const periodQS = computed(() => {
  const parts = [`period=${props.period}`]
  if (props.period === 'custom') {
    if (props.period_from) parts.push(`date_from=${props.period_from}`)
    if (props.period_to)   parts.push(`date_to=${props.period_to}`)
  }
  return parts.join('&')
})

const columns = [
  { key: 'status',   label: t('rentals.columns.status'),   width: '7rem' },
  { key: 'id',       label: t('rentals.columns.id'),       width: '5rem', hideBelow: 'lg' },
  { key: 'customer', label: t('rentals.columns.customer') },
  { key: 'vehicle',  label: t('rentals.columns.vehicle'), hideBelow: 'lg' },
  { key: 'dates',    label: t('rentals.columns.dates'), hideBelow: 'lg' },
  { key: 'deposit_total', label: t('rentals.columns.depositTotal'), numeric: true, align: 'right', hideBelow: 'xl' },
  { key: 'deposit',  label: t('rentals.columns.deposit') },
  { key: 'total',    label: t('rentals.columns.total'),    numeric: true, align: 'right' },
  { key: 'payment',  label: t('rentals.columns.payment'), hideBelow: 'md' },
  { key: 'days',     label: t('rentals.columns.totalDays'), numeric: true, align: 'right', hideBelow: 'lg' },
]

const paymentPill = (status) => {
  if (status === 'cancelled' || status == null) return null
  return {
    paid:        { tone: 'ok',     icon: 'check', label: t('rentals.payment.paid') },
    partial:     { tone: 'warn',   icon: '',      label: t('rentals.payment.partial') },
    written_off: { tone: 'muted',  icon: '',      label: t('rentals.payment.writtenOff') },
    unpaid:      { tone: 'danger', icon: '',      label: t('rentals.payment.unpaid') },
  }[status] || null
}

const depositPill = (status) => {
  if (!status || status === 'none') return { tone: 'warn', icon: '', label: t('rental.show.fields.depositStatus.none') }
  if (status === 'partial')         return { tone: 'warn', icon: '', label: t('rental.show.fields.depositStatus.partial') }
  if (status === 'paid')            return { tone: 'ok',   icon: 'check', label: t('rental.show.fields.depositStatus.paid') }
  return null
}

const overdueHours = (r) => Math.ceil((Date.now() - new Date(r.expected_end_at)) / 3600000)
</script>

<template>
  <PageShell
    :back-href="filters?.from === 'dashboard' ? '/' : ''"
    :back-label="t('common.backToDashboard', 'Dashboard')"
    :title="t('rentals.title', 'Rentals')"
  >
    <template #filters>
      <div class="w-full flex flex-wrap items-center gap-2 md:gap-3">
        <div :class="isAllTime ? 'opacity-40 pointer-events-none select-none' : ''">
          <PeriodSelector
            :current="period"
            :from="period_from"
            :to="period_to"
          />
        </div>
        <div class="hidden xl:block ml-auto">
          <LayoutToggle
            v-model="layoutMode"
            :modes="LAYOUT_MODES"
          />
        </div>
      </div>
      <div class="w-full flex flex-wrap items-center gap-2 md:gap-3">
        <div class="w-full md:w-auto flex flex-wrap items-center gap-2 md:gap-1">
          <div :class="isAllTime ? 'opacity-40 pointer-events-none select-none' : ''">
            <UiSegmented
              :model-value="mainModel"
              :options="statusOptions"
              @update:model-value="filter"
            />
          </div>
          <UiSegmented
            :model-value="allTimeModel"
            :options="allTimeOptions"
            @update:model-value="filter"
          />
        </div>
        <UiInput
          v-model="search"
          type="search"
          icon-left="search"
          :placeholder="t('rentals.searchPlaceholder')"
          class="flex-1 md:flex-none md:w-80 xl:ml-auto min-w-0 max-w-[14rem] sm:min-w-[16rem] sm:max-w-md"
          @update:model-value="onSearch"
        />
        <div class="xl:hidden ml-auto">
          <LayoutToggle
            v-model="layoutMode"
            :modes="LAYOUT_MODES"
          />
        </div>
      </div>
    </template>

    <!-- Table view -->
    <UiTable
      v-if="layoutMode === 'table'"
      responsive-cards
      :columns="columns"
      :rows="rentals"
      :overdue-key="(r) => r.overdue"
      :row-href="(r) => `/rentals/${r.id}`"
      @row-click="(r) => router.visit(`/rentals/${r.id}`)"
    >
      <template #cell-status="{ row }">
        <RentalStatusPill
          :status="row.status"
          :overdue="row.overdue"
        />
      </template>
      <template #cell-id="{ row }">
        <span class="font-mono text-xs text-[color:var(--muted)] tabular-nums">#{{ row.id }}</span>
      </template>
      <template #cell-customer="{ row }">
        <div>
          <p class="font-medium text-[color:var(--ink)]">
            {{ row.customer_name }}
          </p>
          <a
            v-if="row.customer_phone"
            :href="`tel:${row.customer_phone}`"
            class="text-xs text-[color:var(--accent)] hover:underline"
            @click.stop
          >{{ row.customer_phone }}</a>
        </div>
      </template>
      <template #cell-vehicle="{ row }">
        <div>
          <p class="text-[color:var(--ink)]">
            {{ row.vehicle_name }}
          </p>
          <p class="text-xs text-[color:var(--muted)] font-mono">
            {{ row.vehicle_reg }}
          </p>
        </div>
      </template>
      <template #cell-dates="{ row }">
        <div class="text-[color:var(--inkSoft)]">
          <p>{{ formatDateTime(row.start_at) }}</p>
          <p
            class="text-xs"
            :class="row.overdue ? 'text-[color:var(--danger)] font-semibold' : 'text-[color:var(--muted)]'"
          >
            → {{ formatDateTime(row.expected_end_at) }}
            <span
              v-if="row.overdue"
              class="ml-1"
            >
              (+{{ overdueHours(row) >= 24 ? Math.floor(overdueHours(row) / 24) + 'd' : overdueHours(row) + 'h' }} {{ t('rentals.overdue') }})
            </span>
          </p>
        </div>
      </template>
      <template #cell-total="{ row }">
        <span
          v-if="row.status !== 'cancelled' && row.rental_paid_cents > 0"
          class="fw-value text-[color:var(--ink)]"
        >{{ formatMoney(row.rental_paid_cents) }}</span>
        <span
          v-else
          class="text-[color:var(--muted)] text-sm"
          style="font-variant-numeric: normal;"
        >—</span>
      </template>
      <template #cell-payment="{ row }">
        <UiStatusPill
          v-if="row.status !== 'cancelled' && paymentPill(row.payment_status)"
          v-bind="paymentPill(row.payment_status)"
          variant="soft"
        />
        <span
          v-else
          class="text-[color:var(--muted)] text-sm"
          style="font-variant-numeric: normal;"
        >—</span>
      </template>
      <template #cell-deposit="{ row }">
        <UiStatusPill
          v-if="row.status !== 'cancelled' && depositPill(row.deposit_status)"
          v-bind="depositPill(row.deposit_status)"
          variant="soft"
        />
        <span
          v-else
          class="text-[color:var(--muted)] text-sm"
          style="font-variant-numeric: normal;"
        >—</span>
      </template>
      <template #cell-deposit_total="{ row }">
        <span
          v-if="row.status !== 'cancelled' && row.deposit_collected_cents > 0"
          class="fw-value tabular-nums text-[color:var(--ink)]"
        >{{ formatMoney(row.deposit_collected_cents) }}</span>
        <span
          v-else
          class="text-[color:var(--muted)] text-sm"
          style="font-variant-numeric: normal;"
        >—</span>
      </template>
      <template #cell-days="{ row }">
        <span
          v-if="row.status !== 'cancelled' && row.days_rented"
          class="fw-value tabular-nums text-[color:var(--ink)]"
        >{{ row.days_rented }}</span>
        <span
          v-else
          class="text-[color:var(--muted)] text-sm"
          style="font-variant-numeric: normal;"
        >—</span>
      </template>

      <template #card="{ row }">
        <RentalCard :rental="row" />
      </template>

      <template #empty>
        <EmptyState
          :type="search || activeStatus !== 'all' ? 'filter' : 'first-use'"
          :title="search ? `No rentals match &quot;${search}&quot;` : 'No rentals yet'"
          :message="search ? 'Try clearing the search or changing the status filter.' : 'Create your first rental to get started.'"
          @clear="search = ''; filter('all')"
        />
      </template>
    </UiTable>

    <!-- Cards view -->
    <div v-else-if="layoutMode === 'cards'">
      <EmptyState
        v-if="!rentals?.length"
        :type="search || activeStatus !== 'all' ? 'filter' : 'first-use'"
        :title="search ? `No rentals match &quot;${search}&quot;` : 'No rentals yet'"
        :message="search ? 'Try clearing the search or changing the status filter.' : 'Create your first rental to get started.'"
        @clear="search = ''; filter('all')"
      />
      <div
        v-else
        class="grid gap-3"
        style="grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));"
      >
        <Link
          v-for="r in rentals"
          :key="r.id"
          :href="`/rentals/${r.id}`"
          class="block rounded-[var(--radius-lg)] p-4 transition-colors bg-[color:var(--surface)] border border-[color:var(--line)] hover:bg-[color:var(--surfaceAlt)]"
          :class="r.overdue ? 'border-l-[3px] border-l-[color:var(--danger)]' : ''"
        >
          <div class="flex items-center justify-between mb-3">
            <RentalStatusPill
              :status="r.status"
              :overdue="r.overdue"
            />
            <span class="font-mono text-[0.6875rem] text-[color:var(--muted)]">#{{ r.id }}</span>
          </div>
          <div class="font-display text-[1.125rem] truncate text-[color:var(--ink)]">
            {{ r.customer_name }}
          </div>
          <div class="text-[0.75rem] truncate text-[color:var(--muted)]">
            {{ r.vehicle_name }} · {{ r.vehicle_reg }}
          </div>
          <div class="mt-3 flex items-end justify-between">
            <div class="text-[0.6875rem] text-[color:var(--muted)]">
              <div>{{ formatDateTime(r.start_at) }}</div>
              <div :class="r.overdue ? 'text-[color:var(--danger)]' : ''">
                → {{ formatDateTime(r.expected_end_at) }}
              </div>
            </div>
            <div class="font-mono text-[0.9375rem] fw-value tabular-nums text-[color:var(--ink)]">
              {{ formatMoney(r.estimated_total_cents) }}
            </div>
          </div>
        </Link>
      </div>
    </div>

    <!-- Compact view -->
    <div
      v-else-if="layoutMode === 'compact'"
      class="rounded-[var(--radius-lg)] overflow-hidden bg-[color:var(--surface)] border border-[color:var(--line)]"
    >
      <EmptyState
        v-if="!rentals?.length"
        :type="search || activeStatus !== 'all' ? 'filter' : 'first-use'"
        :title="search ? `No rentals match &quot;${search}&quot;` : 'No rentals yet'"
        :message="search ? 'Try clearing the search or changing the status filter.' : 'Create your first rental to get started.'"
        @clear="search = ''; filter('all')"
      />
      <ul
        v-else
        role="list"
        class="divide-y divide-[color:var(--line)]"
      >
        <li
          v-for="r in rentals"
          :key="r.id"
        >
          <Link
            :href="`/rentals/${r.id}`"
            class="flex items-center gap-3 px-4 py-2 transition-colors hover:bg-[color:var(--surfaceAlt)]"
          >
            <span
              class="w-2 h-2 rounded-[var(--radius-full)] flex-shrink-0"
              :class="{
                'bg-[color:var(--ok)]': statusTone(r.status) === 'ok',
                'bg-[color:var(--warn)]': statusTone(r.status) === 'warn',
                'bg-[color:var(--info)]': statusTone(r.status) === 'info',
                'bg-[color:var(--danger)]': statusTone(r.status) === 'danger',
                'bg-[color:var(--muted)]': statusTone(r.status) === 'muted',
              }"
            />
            <span class="font-mono text-[0.6875rem] tabular-nums w-12 text-[color:var(--muted)]">#{{ r.id }}</span>
            <span class="text-[0.8125rem] font-medium truncate flex-1 text-[color:var(--ink)]">{{ r.customer_name }}</span>
            <span
              class="text-[0.75rem] truncate hidden md:inline text-[color:var(--muted)]"
              style="max-width: 200px;"
            >{{ r.vehicle_name }}</span>
            <span class="font-mono text-[0.75rem] tabular-nums text-[color:var(--inkSoft)]">{{ formatMoney(r.estimated_total_cents) }}</span>
            <Icon
              name="chev-r"
              :size="14"
              class="opacity-40"
            />
          </Link>
        </li>
      </ul>
    </div>

    <!-- Pagination -->
    <template
      v-if="meta?.total_pages > 1"
      #footer
    >
      <div class="flex items-center justify-between text-sm text-[color:var(--inkSoft)]">
        <p>Page {{ meta.current_page }} of {{ meta.total_pages }}</p>
        <div class="flex gap-2">
          <Link
            v-if="meta.current_page > 1"
            :href="`/rentals?${periodQS}&page=${meta.current_page - 1}&status=${activeStatus}&q=${search}`"
          >
            <UiButton
              variant="secondary"
              size="sm"
              icon-left="chev-l"
            >
              Previous
            </UiButton>
          </Link>
          <Link
            v-if="meta.current_page < meta.total_pages"
            :href="`/rentals?${periodQS}&page=${meta.current_page + 1}&status=${activeStatus}&q=${search}`"
          >
            <UiButton
              variant="secondary"
              size="sm"
              icon-right="chev-r"
            >
              Next
            </UiButton>
          </Link>
        </div>
      </div>
    </template>
  </PageShell>
</template>
